'use client';

import { c as _c } from "react/compiler-runtime";
import type { EmblaCarouselType } from 'embla-carousel';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { SpartanProductFeature } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/types';
import { useMediaProgress } from '@/app/lcv/components/sections/spartan/shared/hooks/useMediaProgress';
interface UseFeatureImagePlaybackOptions {
  emblaApi?: EmblaCarouselType;
  features: SpartanProductFeature[];
  selectedIndex: number;
  selectedIndexRef: RefObject<number>;
  imageDurationMs: number;
  onImageProgress: (index: number, progress: number) => void;
}
export const useFeatureImagePlayback = t0 => {
  const $ = _c(22);
  const {
    emblaApi,
    features,
    selectedIndex,
    selectedIndexRef,
    imageDurationMs,
    onImageProgress
  } = t0;
  const advanceFrameRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (advanceFrameRef.current == null) {
        return;
      }
      window.cancelAnimationFrame(advanceFrameRef.current);
      advanceFrameRef.current = null;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const cancelScheduledAdvance = t1;
  let t2;
  if ($[1] !== emblaApi || $[2] !== selectedIndexRef) {
    t2 = () => {
      if (!emblaApi) {
        return;
      }
      cancelScheduledAdvance();
      const completedIndex = selectedIndexRef.current;
      advanceFrameRef.current = window.requestAnimationFrame(() => {
        advanceFrameRef.current = null;
        if (selectedIndexRef.current !== completedIndex) {
          return;
        }
        emblaApi.scrollNext();
      });
    };
    $[1] = emblaApi;
    $[2] = selectedIndexRef;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const scheduleNextFrame = t2;
  let t3;
  if ($[4] !== onImageProgress || $[5] !== selectedIndexRef) {
    t3 = progress => {
      onImageProgress(selectedIndexRef.current, progress);
    };
    $[4] = onImageProgress;
    $[5] = selectedIndexRef;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleImageProgress = t3;
  const t4 = features[selectedIndex]?.durationMs ?? imageDurationMs;
  let t5;
  if ($[7] !== handleImageProgress || $[8] !== scheduleNextFrame || $[9] !== t4) {
    t5 = {
      durationMs: t4,
      onProgress: handleImageProgress,
      onComplete: scheduleNextFrame
    };
    $[7] = handleImageProgress;
    $[8] = scheduleNextFrame;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    isCompleted,
    start,
    pause,
    reset
  } = useMediaProgress(t5);
  let t6;
  if ($[11] !== pause) {
    t6 = () => {
      pause();
      cancelScheduledAdvance();
    };
    $[11] = pause;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const pausePlayback = t6;
  let t7;
  if ($[13] !== reset) {
    t7 = () => {
      reset();
      cancelScheduledAdvance();
    };
    $[13] = reset;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const resetPlayback = t7;
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => cancelScheduledAdvance;
    t9 = [cancelScheduledAdvance];
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  if ($[17] !== isCompleted || $[18] !== pausePlayback || $[19] !== resetPlayback || $[20] !== start) {
    t10 = {
      isCompleted,
      startPlayback: start,
      pausePlayback,
      resetPlayback,
      cancelScheduledAdvance
    };
    $[17] = isCompleted;
    $[18] = pausePlayback;
    $[19] = resetPlayback;
    $[20] = start;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
};